// src/pages/admin/escanear-asistencia.tsx
// REFACTORIZADO: Usa PageTitle y LoadingStates para consistencia visual

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';

// Componentes reutilizables
import PageTitle from '../../components/page-title';
import { LoadingMessage, ErrorState } from '../../components/loading-states';
import {
  Camera,
  CheckCircle,
  XCircle,
  RefreshCw,
  StopCircle,
  Users,
  Award,
  Clock,
  AlertCircle
} from 'lucide-react';

interface AsistenciaRegistrada {
  nombre: string;
  taller: string;
  creditos: number;
  fecha: Date;
}

export default function EscanearAsistencia() {
  const { user } = useUser();
  const navigate = useNavigate();
  const scannerRef = useRef<HTMLDivElement | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const [estado, setEstado] = useState<'esperando' | 'ok' | 'error' | 'procesando'>('esperando');
  const [ultimaAsistencia, setUltimaAsistencia] = useState<AsistenciaRegistrada | null>(null);
  const [scanner, setScanner] = useState<Html5Qrcode | null>(null);
  const [loading, setLoading] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);

  // Estadísticas de la sesión
  const [stats, setStats] = useState({
    exitosos: 0,
    errores: 0,
    duplicados: 0
  });

  const procesarQR = async (decodedText: string) => {
    // Evitar procesar el mismo código múltiples veces
    if (estado === 'procesando') return;

    setResultado(decodedText);
    setEstado('procesando');

    try {
      // Buscar inscripción por código QR
      const q = query(collection(db, 'inscripcion'), where('codigo_qr', '==', decodedText));
      const snap = await getDocs(q);
      let snapExt: any = null;

      if (snap.empty) {
        // Intentar también en inscripciones externas
        const qExt = query(collection(db, 'externo_inscripcion'), where('codigo_qr', '==', decodedText));
        snapExt = await getDocs(qExt);

        if (snapExt.empty) {
          setEstado('error');
          setStats(prev => ({ ...prev, errores: prev.errores + 1 }));

          Swal.fire({
            title: 'Código QR No Válido',
            text: 'No se encontró una inscripción asociada a este código QR.',
            icon: 'error',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Entendido'
          });
          return;
        }
      }

      const docData = snap.empty ? snapExt.docs[0] : snap.docs[0];
      const data = docData.data();
      const collection_name = snap.empty ? 'externo_inscripcion' : 'inscripcion';

      // Verificar si ya tiene asistencia registrada
      if (data.asistencia) {
        setEstado('error');
        setStats(prev => ({ ...prev, duplicados: prev.duplicados + 1 }));

        const fechaAsistencia = data.fecha_asistencia
          ? new Date(data.fecha_asistencia.seconds * 1000).toLocaleString('es-MX')
          : 'Fecha no disponible';

        Swal.fire({
          title: 'Asistencia Ya Registrada',
          html: `
            <div class="text-start">
              <p><strong>Participante:</strong> ${data.nombre || 'Usuario'}</p>
              <p><strong>Registrada el:</strong> ${fechaAsistencia}</p>
              <p class="text-warning mb-0">⚠️ Esta asistencia ya fue contabilizada.</p>
            </div>
          `,
          icon: 'warning',
          confirmButtonColor: '#ffc107',
          confirmButtonText: 'Entendido'
        });
        return;
      }

      // Obtener información del taller
      let tallerInfo = null;
      if (data.taller_id) {
        const tallerDoc = await getDoc(doc(db, 'taller', data.taller_id));
        if (tallerDoc.exists()) {
          tallerInfo = tallerDoc.data();
        }
      }

      // Registrar asistencia
      const docRef = doc(db, collection_name, docData.id);
      const fechaRegistro = new Date();

      await updateDoc(docRef, {
        asistencia: true,
        completado: true,
        creditos: data.creditos || tallerInfo?.creditos || 0,
        fecha_asistencia: fechaRegistro,
        registrado_por: user?.nombre || 'Admin',
        registrado_por_uid: user?.uid || ''
      });

      // Guardar info para mostrar
      const asistenciaInfo: AsistenciaRegistrada = {
        nombre: data.nombre || 'Sin nombre',
        taller: tallerInfo?.nombre || 'Taller no especificado',
        creditos: data.creditos || tallerInfo?.creditos || 0,
        fecha: fechaRegistro
      };

      setUltimaAsistencia(asistenciaInfo);
      setEstado('ok');
      setStats(prev => ({ ...prev, exitosos: prev.exitosos + 1 }));

      // Sonido de éxito (opcional)
      try {
        const audio = new Audio('data:audio/wav;base64,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');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {
        // Ignorar errores de audio
      }

      Swal.fire({
        title: '¡Asistencia Registrada!',
        html: `
          <div class="text-start">
            <p><strong>Participante:</strong> ${asistenciaInfo.nombre}</p>
            <p><strong>Curso:</strong> ${asistenciaInfo.taller}</p>
            <p><strong>Créditos:</strong> ${asistenciaInfo.creditos}</p>
            <p class="text-success mb-0">✅ Registro exitoso</p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#28a745',
        timer: 3000,
        confirmButtonText: 'Entendido'
      });

      // Reiniciar automáticamente después de 2 segundos
      setTimeout(() => {
        if (estado === 'ok') {
          setEstado('esperando');
          setResultado(null);
        }
      }, 2000);

    } catch (error) {
      console.error('Error al procesar QR:', error);
      setEstado('error');
      setStats(prev => ({ ...prev, errores: prev.errores + 1 }));

      Swal.fire({
        title: 'Error al Procesar',
        text: 'Ocurrió un error al registrar la asistencia. Inténtalo de nuevo.',
        icon: 'error',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Entendido'
      });
    }
  };

  const iniciarEscaner = async () => {
    if (!scannerRef.current) return;

    try {
      const newScanner = new Html5Qrcode('reader');

      await newScanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        procesarQR,
        (errorMessage) => {
          // Solo log en desarrollo
          if (process.env.NODE_ENV === 'development') {
            // console.warn('QR no detectado aún:', errorMessage);
          }
        }
      );

      setScanner(newScanner);
      setCameraActive(true);
      setLoading(false);

    } catch (error) {
      console.error('Error al iniciar escaner:', error);
      setLoading(false);
      setCameraActive(false);

      Swal.fire({
        title: 'Error de Cámara',
        html: `
          <div class="text-start">
            <p>No se pudo acceder a la cámara.</p>
            <p><strong>Posibles soluciones:</strong></p>
            <ul>
              <li>Verifica que el navegador tenga permisos de cámara</li>
              <li>Asegúrate de estar usando HTTPS o localhost</li>
              <li>Cierra otras aplicaciones que puedan estar usando la cámara</li>
            </ul>
          </div>
        `,
        icon: 'error',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Entendido'
      });
    }
  };

  const detenerEscaner = async () => {
    if (scanner) {
      try {
        await scanner.stop();
        scanner.clear();
        setScanner(null);
        setCameraActive(false);
      } catch (error) {
        console.error('Error al detener escaner:', error);
      }
    }
  };

  const reiniciarEscaner = async () => {
    setEstado('esperando');
    setResultado(null);
    setUltimaAsistencia(null);
    await detenerEscaner();
    setTimeout(() => {
      iniciarEscaner();
    }, 500);
  };

  useEffect(() => {
    // Validar acceso - permitir admin o admin de comité
    const tieneAcceso = user?.tipo?.includes('admin') || user?.esAdminComite === true;
    if (!user || !tieneAcceso) {
      Swal.fire({
        icon: 'error',
        title: 'Acceso denegado',
        text: 'No tienes permiso para acceder a esta sección.',
        confirmButtonColor: '#dc3545'
      });
      navigate('/dashboard');
      return;
    }

    iniciarEscaner();

    // Cleanup al desmontar componente
    return () => {
      detenerEscaner();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <>
        <UserNav />
        <PageTitle
          title="Escanear Asistencia QR"
          subtitle="Iniciando escáner de códigos QR..."
        />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage
              message="Iniciando escáner de códigos QR..."
              subMessage="Espera mientras se activa la cámara"
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Validar acceso - permitir admin o admin de comité
  const tieneAccesoRender = user?.tipo?.includes('admin') || user?.esAdminComite === true;
  if (!user || !tieneAccesoRender) {
    return (
      <>
        <UserNav />
        <PageTitle
          title="Acceso Denegado"
          subtitle="No tienes permiso para acceder a esta sección"
        />
        <section className="gray-simple">
          <div className="container py-5">
            <ErrorState
              title="Acceso Denegado"
              message="No tienes permiso para acceder a esta sección"
              onRetry={() => navigate('/dashboard')}
              retryText="Volver al Dashboard"
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      <UserNav />

      <PageTitle
        title="Escanear Asistencia QR"
        subtitle="Escanea el código QR del participante para registrar su asistencia"
      />

      <section className="gray-simple">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Escanear Asistencia' }
            ]}
          />

          {/* Estadísticas de la sesión */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <CheckCircle className="text-success" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Exitosos</h6>
                      <h3 className="mb-0">{stats.exitosos}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-warning bg-opacity-10 p-3 rounded">
                        <AlertCircle className="text-warning" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Duplicados</h6>
                      <h3 className="mb-0">{stats.duplicados}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="bg-danger bg-opacity-10 p-3 rounded">
                        <XCircle className="text-danger" size={24} />
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h6 className="text-muted mb-0">Errores</h6>
                      <h3 className="mb-0">{stats.errores}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Escáner */}
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  {/* Estado del escáner */}
                  <div className="text-center mb-4">
                    <h5 className="card-title d-flex align-items-center justify-content-center gap-2">
                      {estado === 'esperando' && (
                        <>
                          <Camera size={24} className="text-primary" />
                          <span>Apunta la cámara al código QR</span>
                        </>
                      )}
                      {estado === 'procesando' && (
                        <>
                          <div className="spinner-border spinner-border-sm text-info" role="status"></div>
                          <span>Procesando...</span>
                        </>
                      )}
                      {estado === 'ok' && (
                        <>
                          <CheckCircle size={24} className="text-success" />
                          <span>¡Asistencia Registrada!</span>
                        </>
                      )}
                      {estado === 'error' && (
                        <>
                          <XCircle size={24} className="text-danger" />
                          <span>Error en el escaneo</span>
                        </>
                      )}
                    </h5>
                  </div>

                  {/* Visor del escáner */}
                  <div
                    id="reader"
                    ref={scannerRef}
                    style={{
                      width: '100%',
                      maxWidth: 500,
                      margin: 'auto',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                    className="mb-3"
                  />

                  {/* Información de la última asistencia */}
                  {estado === 'ok' && ultimaAsistencia && (
                    <div className="alert alert-success border-0 shadow-sm">
                      <div className="d-flex align-items-start">
                        <CheckCircle size={24} className="text-success me-3 mt-1" />
                        <div className="flex-grow-1">
                          <h6 className="alert-heading mb-2">Asistencia Registrada Exitosamente</h6>
                          <div className="row g-2">
                            <div className="col-md-6">
                              <small className="text-muted d-block">
                                <Users size={14} className="me-1" />
                                Participante
                              </small>
                              <strong>{ultimaAsistencia.nombre}</strong>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted d-block">
                                <Award size={14} className="me-1" />
                                Curso
                              </small>
                              <strong>{ultimaAsistencia.taller}</strong>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted d-block">
                                <Award size={14} className="me-1" />
                                Créditos
                              </small>
                              <strong>{ultimaAsistencia.creditos}</strong>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted d-block">
                                <Clock size={14} className="me-1" />
                                Hora de registro
                              </small>
                              <strong>{ultimaAsistencia.fecha.toLocaleTimeString('es-MX')}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Estado de procesamiento */}
                  {estado === 'procesando' && (
                    <div className="alert alert-info border-0 shadow-sm">
                      <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        <span>Verificando inscripción y registrando asistencia...</span>
                      </div>
                    </div>
                  )}

                  {/* Código escaneado */}
                  {resultado && (
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        <strong>Último código escaneado:</strong>{' '}
                        <code className="bg-light px-2 py-1 rounded">
                          {resultado.slice(0, 30)}...
                        </code>
                      </small>
                    </div>
                  )}

                  {/* Botones de control */}
                  <div className="mt-4 d-flex justify-content-center gap-2 flex-wrap">
                    {(estado === 'ok' || estado === 'error') ? (
                      <button
                        className="btn btn-primary"
                        onClick={reiniciarEscaner}
                        disabled={!cameraActive}
                      >
                        <RefreshCw size={16} className="me-2" />
                        Escanear Nuevo QR
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-primary"
                        onClick={reiniciarEscaner}
                        disabled={estado === 'procesando'}
                      >
                        <RefreshCw size={16} className="me-2" />
                        Reiniciar Escáner
                      </button>
                    )}
                    <button
                      className="btn btn-outline-danger"
                      onClick={detenerEscaner}
                      disabled={!cameraActive}
                    >
                      <StopCircle size={16} className="me-2" />
                      Detener Cámara
                    </button>
                  </div>

                  {/* Estado de la cámara */}
                  <div className="mt-3 text-center">
                    <small className={`badge ${cameraActive ? 'bg-success' : 'bg-secondary'}`}>
                      {cameraActive ? '● Cámara Activa' : '○ Cámara Inactiva'}
                    </small>
                  </div>
                </div>
              </div>

              {/* Instrucciones */}
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-body">
                  <h6 className="card-title">
                    <AlertCircle size={18} className="me-2 text-info" />
                    Instrucciones de uso
                  </h6>
                  <ul className="mb-0 small text-muted">
                    <li>Asegúrate de que el código QR esté bien iluminado y enfocado</li>
                    <li>Mantén el código QR dentro del área de escaneo (cuadrado verde)</li>
                    <li>La asistencia se registrará automáticamente al detectar un código válido</li>
                    <li>Si el código ya fue escaneado, aparecerá una advertencia de duplicado</li>
                    <li>Puedes ver las estadísticas de la sesión actual en las tarjetas superiores</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
